import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import PauseCircleIcon from '@assets/icons/pause-circle.svg';
import React, { useMemo, useState } from 'react';
import SettingIcon from '@assets/icons/settings.svg';
import SubscriptionsEmptyIcon from '@assets/icons/subscriptions-outlined.svg';
import TrashIcon from '@assets/icons/trash-small-thin.svg';
import { APP_MODAL_ROUTES, ORDER_ROUTES } from '@/navigators/routes';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { ConfirmationActionContent } from '@/components/confirmation-action-content';
import { createStyles } from '@/contexts/theme-context';
import {
  DeleteVp_SubscriptionMutation,
  DeleteVp_SubscriptionMutationVariables,
  GetNextDeliveryDateFor_SubscriptionQuery,
  GetNextDeliveryDateFor_SubscriptionQueryVariables,
  GetRestaurantVp_SubscriptionsQuery,
  GetRestaurantVp_SubscriptionsQueryVariables,
  RestaurantVp_SubscriptionFragment,
  ResumeVp_SubscriptionMutation,
  ResumeVp_SubscriptionMutationVariables,
  SkipNextVp_SubscriptionMutation,
  SkipNextVp_SubscriptionMutationVariables,
} from '@/types';
import { formatDate } from '@/utils/date';
import {
  GET_NEXT_DELIVERY_DATE_FOR_SUBSCRIPTION,
  GET_RESTAURANT_VP_SUBSCRIPTIONS,
  RESTAURANT_VP_SUBSCRIPTION_FRAGMENT,
} from './queries';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import { ItemListOption } from '@/contexts/item-context-types';
import { List } from '@/components/list';
import { Props as ListItemProps, ListOption } from '@/components/list-item';
import { ListSection, RecyclerList } from '@/components/recycler-list';
import { Props as NavigationHeaderProps } from '@/components/navigation-header';
import { OrdersStackParams } from '@/navigators/types';
import { PageContainer } from '@/components/page-container';
import { RouteProp } from '@react-navigation/native';
import { SCREEN_PADDING, SCREEN_PADDING_SMALL, TYPOGRAPHY } from '@/styles';
import { StackNavigationProp } from '@react-navigation/stack';
import { StatusBar, Text, TouchableOpacity, View } from 'react-native';
import { SubscriptionListItem } from '@/components/subscriptions/subscription-list-item';
import { SubscriptionPauseUntil } from '@/components/subscriptions/subscription-pause-until';
import { SubscriptionsLoader } from '@/components/subscriptions/subscriptions-loader';
import { SvgProps } from 'react-native-svg';
import { Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAdaptVariantPackToItemListOption } from '@/contexts/item-context-utils';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useItemQueryVariables } from '@/hooks/use-item-query-variables';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const SKIP_NEXT_RESTAURANT_SUBSCRIPTION_MUTATION = gql`
  mutation SkipNextVP_Subscription(
    $restaurantUUID: uuid!
    $restaurantVPSubscriptionUUID: uuid!
    $supplierUUID: uuid!
  ) {
    skipNextRestaurantVariantPackSubscription(
      restaurant_id: $restaurantUUID
      restaurant_variant_pack_subscription_id: $restaurantVPSubscriptionUUID
      supplier_id: $supplierUUID
    ) {
      restaurant_variant_pack_subscription {
        ...RestaurantVP_Subscription
      }
    }
  }

  ${RESTAURANT_VP_SUBSCRIPTION_FRAGMENT}
`;

export const RESUME_RESTAURANT_SUBSCRIPTION_MUTATION = gql`
  mutation ResumeVP_Subscription(
    $restaurantUUID: uuid!
    $restaurantVPSubscriptionUUID: uuid!
    $supplierUUID: uuid!
  ) {
    removeRestaurantVariantPackSubscriptionPause(
      restaurant_id: $restaurantUUID
      restaurant_variant_pack_subscription_id: $restaurantVPSubscriptionUUID
      supplier_id: $supplierUUID
    ) {
      restaurant_variant_pack_subscription {
        ...RestaurantVP_Subscription
      }
    }
  }

  ${RESTAURANT_VP_SUBSCRIPTION_FRAGMENT}
`;

export const DELETE_RESTAURANT_SUBSCRIPTION_MUTATION = gql`
  mutation DeleteVP_Subscription(
    $restaurantUUID: uuid!
    $restaurantVPSubscriptionUUID: uuid!
    $supplierUUID: uuid!
  ) {
    deleteRestaurantVariantPackSubscription(
      restaurant_id: $restaurantUUID
      restaurant_variant_pack_subscription_id: $restaurantVPSubscriptionUUID
      supplier_id: $supplierUUID
    ) {
      success
    }
  }
`;

type SubscriptionMenuOperationOption = ListOption<{
  Icon: React.FC<SvgProps>;
  iconProps?: SvgProps;
}>;

interface SubscriptionItemType {
  metadata: {
    nextDeliveryDate: string;
    subscription?: RestaurantVp_SubscriptionFragment | null;
    variantPack?: ItemListOption | null;
  };
  title: string;
  value: string;
}

enum SubscriptionActionsRoute {
  Delete = 'DELETE',
  Pause = 'PAUSE',
  PauseUntilSpecificDay = 'PAUSE_UNTIL_SPECIFIC_DAY',
  Resume = 'RESUME',
  SelectOperation = 'SELECT_OPERATION',
  SkipNextOrder = 'SKIP_NEXT_ORDER',
  SubscriptionSettings = 'SUBSCRIPTION_SETTINGS',
}

interface Props {
  navigation: StackNavigationProp<
    OrdersStackParams,
    ORDER_ROUTES.Subscriptions
  >;
  route: RouteProp<OrdersStackParams, ORDER_ROUTES.Subscriptions>;
}

export const SubscriptionsScreen: React.FC<Props> = ({ navigation, route }) => {
  const { params } = route;

  const [selectedSubscriptionUUID, setSelectedSubscriptionUUID] = useState<
    string | null
  >(null);

  const [subscriptionMessageVisible, setSubscriptionMessageVisible] = useState(
    Boolean(params?.message) && Boolean(params?.title),
  );

  const closeSubscriptionIssueMessage = () => {
    setSubscriptionMessageVisible(false);
  };

  const itemQueryVariables = useItemQueryVariables();
  const { isSmallLayout, isLargeLayout } = useLayoutContext();
  const { selectItemListOption } = useItemDetailContext();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { showErrorToast, showToast } = useToasts();

  const refetchQueries = [
    getOperationName(GET_RESTAURANT_VP_SUBSCRIPTIONS) as string,
  ];

  const [
    skipNextRestaurantVPSubscription,
    { loading: skipNextSubscriptionLoading },
  ] = useMutation<
    SkipNextVp_SubscriptionMutation,
    SkipNextVp_SubscriptionMutationVariables
  >(SKIP_NEXT_RESTAURANT_SUBSCRIPTION_MUTATION, {
    refetchQueries,
  });

  const [
    resumeRestaurantVPSubscription,
    { loading: resumeSubscriptionLoading },
  ] = useMutation<
    ResumeVp_SubscriptionMutation,
    ResumeVp_SubscriptionMutationVariables
  >(RESUME_RESTAURANT_SUBSCRIPTION_MUTATION, {
    refetchQueries,
  });

  const [
    deleteRestaurantVPSubscription,
    { loading: deleteSubscriptionLoading },
  ] = useMutation<
    DeleteVp_SubscriptionMutation,
    DeleteVp_SubscriptionMutationVariables
  >(DELETE_RESTAURANT_SUBSCRIPTION_MUTATION, {
    refetchQueries,
  });

  const [styles, theme] = useStyles();
  const navigationHeaderProps: NavigationHeaderProps = useMemo(
    () => ({
      backButtonLabel: !isSmallLayout
        ? i18n.t('back_to', { name: i18n.t('orders') })
        : undefined,
      centerTitle: isSmallLayout,
      responsive: true,
      showBackButton: isSmallLayout,
      showShadow: false,
      title: i18n.t('subscriptions'),
    }),
    [isSmallLayout],
  );

  const { data, loading } = useWrappedQuery<
    GetRestaurantVp_SubscriptionsQuery,
    GetRestaurantVp_SubscriptionsQueryVariables
  >(GET_RESTAURANT_VP_SUBSCRIPTIONS, {
    fetchPolicy: 'network-only',
    variables: {
      ...itemQueryVariables,
      restaurantUUID,
      supplierUUID,
    },
  });

  const adaptVariantPackToItemListOption =
    useAdaptVariantPackToItemListOption();

  const sections = useMemo<ListSection<SubscriptionItemType>[]>(
    () => [
      {
        data: (data?.getRestaurantVariantPackSubscriptions ?? []).map(
          ({
            restaurant_variant_pack_subscription,
            next_delivery_date,
            restaurant_variant_pack_subscription_id,
          }) => ({
            metadata: {
              nextDeliveryDate: next_delivery_date,
              subscription: restaurant_variant_pack_subscription,
              variantPack: restaurant_variant_pack_subscription?.variant_pack
                ? adaptVariantPackToItemListOption(
                    restaurant_variant_pack_subscription.variant_pack,
                  )
                : null,
            },
            title: restaurant_variant_pack_subscription_id,
            value: restaurant_variant_pack_subscription_id,
          }),
        ),
        key: null,
      },
    ],
    [
      adaptVariantPackToItemListOption,
      data?.getRestaurantVariantPackSubscriptions,
    ],
  );

  const selectedSubscription = useMemo(() => {
    return (data?.getRestaurantVariantPackSubscriptions ?? []).find(
      (subscription) =>
        subscription.restaurant_variant_pack_subscription_id ===
        selectedSubscriptionUUID,
    );
  }, [data?.getRestaurantVariantPackSubscriptions, selectedSubscriptionUUID]);

  const adaptedSubscriptionVP = selectedSubscription
    ?.restaurant_variant_pack_subscription?.variant_pack
    ? adaptVariantPackToItemListOption(
        selectedSubscription.restaurant_variant_pack_subscription.variant_pack,
      )
    : null;

  const nextFulfillmentDateIfSkip = useMemo(() => {
    if (!selectedSubscription) {
      return;
    }
    const currentFulfillmentMoment = moment(
      selectedSubscription.next_delivery_date,
    );
    const skipDays =
      selectedSubscription.restaurant_variant_pack_subscription?.config
        .frequency_days ?? 0;
    return formatDate(currentFulfillmentMoment.add(skipDays, 'days'), {
      day: 'numeric',
      month: 'short',
      weekday: 'short',
    });
  }, [selectedSubscription]);

  const nextDeliveryDateVariables = useMemo(() => {
    const restaurantVariantPackSubscription =
      selectedSubscription?.restaurant_variant_pack_subscription;

    if (restaurantVariantPackSubscription) {
      return {
        frequencyDays: restaurantVariantPackSubscription.config.frequency_days,
        fulfillmentSettingsUUID:
          restaurantVariantPackSubscription.fulfillment_settings_uuid,
        orderDay:
          restaurantVariantPackSubscription.config.weekday.toUpperCase(),
        restaurantUUID,
        restaurantVPSubscriptionUUID: selectedSubscriptionUUID,
        supplierUUID,
      };
    }
  }, [
    restaurantUUID,
    selectedSubscriptionUUID,
    selectedSubscription,
    supplierUUID,
  ]);

  const { data: nextDeliveryDateData, loading: nextDeliveryDateLoading } =
    useWrappedQuery<
      GetNextDeliveryDateFor_SubscriptionQuery,
      GetNextDeliveryDateFor_SubscriptionQueryVariables
    >(GET_NEXT_DELIVERY_DATE_FOR_SUBSCRIPTION, {
      fetchPolicy: 'network-only',
      skip: !nextDeliveryDateVariables,
      variables: nextDeliveryDateVariables,
    });

  const nextFulfillmentDateIfResume = useMemo(() => {
    if (!nextDeliveryDateData) {
      return;
    }

    const mextPlacementMoment = moment(
      nextDeliveryDateData.getNextDeliveryDateForSubscriptionSettings
        .next_placement_date,
    );

    return formatDate(mextPlacementMoment, {
      day: 'numeric',
      month: 'short',
      weekday: 'short',
    });
  }, [nextDeliveryDateData]);

  const isSelectedSubscriptionOnPause = useMemo(() => {
    if (
      selectedSubscription?.restaurant_variant_pack_subscription?.pause_until
    ) {
      const pauseUntil = moment(
        selectedSubscription.restaurant_variant_pack_subscription.pause_until,
      );
      return moment().isSameOrBefore(pauseUntil);
    }
    return false;
  }, [selectedSubscription?.restaurant_variant_pack_subscription?.pause_until]);

  const listItemStyle = useSmallLayoutSizeValue(
    [styles.listItem, styles.listItemSmall],
    [styles.listItem, styles.listItemLarge],
  );

  const listItemProps: Partial<ListItemProps<SubscriptionItemType>> = {
    listItemStyle,
    renderMainContent: ({ metadata }) => {
      if (metadata.subscription && metadata.variantPack) {
        return (
          <TouchableOpacity
            onPress={() =>
              selectItemListOption(metadata.variantPack!, {
                source: 'subscription_item',
              })
            }
            style={styles.flex}
          >
            <SubscriptionListItem
              nextDeliveryDate={metadata.nextDeliveryDate}
              onSubscriptionOptionsSelect={setSelectedSubscriptionUUID}
              subscription={metadata.subscription}
              variantPack={metadata.variantPack}
            />
          </TouchableOpacity>
        );
      }
    },
  };

  const subscriptionMenuOperations: SubscriptionMenuOperationOption[] = useMemo(
    () => [
      isSelectedSubscriptionOnPause
        ? {
            metadata: {
              Icon: PauseCircleIcon,
              iconProps: {
                stroke: theme.sk.color.icon.primary,
              },
            },
            title: i18n.t('resume_orders'),
            value: SubscriptionActionsRoute.Resume,
          }
        : {
            metadata: {
              Icon: PauseCircleIcon,
              iconProps: {
                stroke: theme.sk.color.icon.primary,
              },
            },
            title: i18n.t('pause'),
            value: SubscriptionActionsRoute.Pause,
          },
      {
        metadata: {
          Icon: SettingIcon,
          iconProps: {
            stroke: isSelectedSubscriptionOnPause
              ? theme.sk.color.icon.disabled
              : theme.sk.color.icon.primary,
          },
        },
        title: i18n.t('subscription_settings'),
        value: SubscriptionActionsRoute.SubscriptionSettings,
      },
      {
        metadata: {
          Icon: TrashIcon,
          iconProps: {
            stroke: theme.sk.color.icon.negative.primary,
          },
        },
        title: i18n.t('delete'),
        value: SubscriptionActionsRoute.Delete,
      },
    ],
    [isSelectedSubscriptionOnPause, theme],
  );

  const pauseOptions = useMemo(
    () => [
      {
        title: i18n.t('skip_next_order'),
        value: SubscriptionActionsRoute.SkipNextOrder,
      },
      {
        title: i18n.t('pause_until_specific_day'),
        value: SubscriptionActionsRoute.PauseUntilSpecificDay,
      },
    ],
    [],
  );

  const closeSubscriptionActionSheet = () => setSelectedSubscriptionUUID(null);

  return (
    <>
      <StatusBar backgroundColor={theme.colors.white} barStyle="dark-content" />
      <PageContainer navigationHeaderProps={navigationHeaderProps}>
        <View style={[styles.base, styles.flex]}>
          {loading ? (
            <SubscriptionsLoader />
          ) : (data?.getRestaurantVariantPackSubscriptions ?? []).length > 0 ? (
            <RecyclerList
              contentContainerStyle={styles.contentContainer}
              estimatedItemSize={142}
              listItemProps={listItemProps}
              listItemSeparatorProps={{
                style: styles.separator,
              }}
              numColumns={isLargeLayout ? 2 : 1}
              sections={sections}
            />
          ) : (
            <View style={[styles.emptyState, styles.flex]}>
              <SubscriptionsEmptyIcon />
              <Text style={[TYPOGRAPHY.medium, styles.messageText]}>
                {i18n.t('subscriptions.empty_state')}
              </Text>
            </View>
          )}
        </View>
      </PageContainer>
      {!selectedSubscriptionUUID || !selectedSubscription ? null : (
        <CombinedActionSheet
          animateTransitions
          contentStyle={styles.actionSheetContent}
          hasStickyHeader={false}
          initialRoute={SubscriptionActionsRoute.SelectOperation}
          onClose={closeSubscriptionActionSheet}
          onRouteChange={(routeKey) => {
            if (
              routeKey === SubscriptionActionsRoute.SubscriptionSettings &&
              selectedSubscription.restaurant_variant_pack_subscription &&
              adaptedSubscriptionVP?.metadata
            ) {
              const {
                restaurant_variant_pack_subscription,
                next_delivery_date,
              } = selectedSubscription;
              closeSubscriptionActionSheet();
              navigation.navigate(APP_MODAL_ROUTES.EditSubscription, {
                frequencyDays:
                  restaurant_variant_pack_subscription.config.frequency_days,
                fulfillmentMethodUUID:
                  restaurant_variant_pack_subscription.fulfillment_settings_uuid,
                metadata: adaptedSubscriptionVP.metadata,
                nextAvailableDate: next_delivery_date,
                orderDay: restaurant_variant_pack_subscription.config.weekday,
                quantity: restaurant_variant_pack_subscription.config.quantity,
                restaurantVPSubscriptionUUID: selectedSubscriptionUUID,
              });
            }
          }}
          renderScene={({ jumpTo, routeKey }) => {
            switch (routeKey) {
              case SubscriptionActionsRoute.Delete:
                return (
                  <ConfirmationActionContent
                    loading={deleteSubscriptionLoading}
                    message={i18n.t('this_action_can_not_be_undone')}
                    onPrimaryButtonPress={async () => {
                      try {
                        await deleteRestaurantVPSubscription({
                          variables: {
                            restaurantUUID,
                            restaurantVPSubscriptionUUID:
                              selectedSubscriptionUUID,
                            supplierUUID,
                          },
                        });
                        showToast({
                          content: i18n.t('subscription_deleted_successfully'),
                          type: Type.POSITIVE,
                        });
                      } catch {
                        showErrorToast();
                      } finally {
                        setSelectedSubscriptionUUID(null);
                      }
                    }}
                    onSecondaryButtonPress={() =>
                      setSelectedSubscriptionUUID(null)
                    }
                    primaryButtonLabel={i18n.t('delete')}
                    primaryButtonTrackingData={{
                      name: 'proceed_delete_subscription',
                      properties: {
                        subscriptionUUID: selectedSubscriptionUUID,
                      },
                    }}
                    primaryButtonType="danger"
                    secondaryButtonLabel={i18n.t('cancel')}
                    secondaryButtonTrackingData={{
                      name: 'cancel_delete_subscription',
                      properties: {
                        subscriptionUUID: selectedSubscriptionUUID,
                      },
                    }}
                    title={i18n.t('delete_subscription_confirmation.title')}
                  />
                );
              case SubscriptionActionsRoute.Pause:
                return (
                  <List
                    data={pauseOptions}
                    onSelect={(item) => {
                      jumpTo(item.value);
                    }}
                  />
                );
              case SubscriptionActionsRoute.SubscriptionSettings:
                return null;
              case SubscriptionActionsRoute.SelectOperation:
                return (
                  <List
                    data={subscriptionMenuOperations}
                    listItemTitleStyle={(item) => {
                      if (item.value === SubscriptionActionsRoute.Delete) {
                        return {
                          color: theme.sk.color.text.error,
                        };
                      }
                      if (
                        item.value ===
                          SubscriptionActionsRoute.SubscriptionSettings &&
                        isSelectedSubscriptionOnPause
                      ) {
                        return {
                          color: theme.sk.color.text.disabled,
                        };
                      }
                    }}
                    onSelect={(item) => {
                      if (
                        item.value ===
                          SubscriptionActionsRoute.SubscriptionSettings &&
                        isSelectedSubscriptionOnPause
                      ) {
                        return;
                      }
                      jumpTo(item.value as SubscriptionActionsRoute);
                    }}
                    renderLeftComponent={(item) => {
                      const Icon = item.metadata!.Icon;
                      return <Icon {...item.metadata!.iconProps} />;
                    }}
                  />
                );
              case SubscriptionActionsRoute.SkipNextOrder:
                return (
                  <ConfirmationActionContent
                    loading={skipNextSubscriptionLoading}
                    message={i18n.t('skip_next_order.confirmation_message', {
                      date: nextFulfillmentDateIfSkip,
                    })}
                    onPrimaryButtonPress={async () => {
                      try {
                        await skipNextRestaurantVPSubscription({
                          variables: {
                            restaurantUUID,
                            restaurantVPSubscriptionUUID:
                              selectedSubscriptionUUID,
                            supplierUUID,
                          },
                        });
                      } catch {
                        showErrorToast();
                      } finally {
                        closeSubscriptionActionSheet();
                      }
                    }}
                    onSecondaryButtonPress={closeSubscriptionActionSheet}
                    primaryButtonLabel={i18n.t('skip')}
                    primaryButtonTrackingData={{
                      name: 'proceed_skip_next_order',
                      properties: {
                        subscriptionUUID: selectedSubscriptionUUID,
                      },
                    }}
                    secondaryButtonLabel={i18n.t('cancel')}
                    secondaryButtonTrackingData={{
                      name: 'cancel_skip_next_order',
                      properties: {
                        subscriptionUUID: selectedSubscriptionUUID,
                      },
                    }}
                    title={i18n.t('skip_next_order.confirmation_title')}
                  />
                );
              case SubscriptionActionsRoute.PauseUntilSpecificDay:
                return selectedSubscription ? (
                  <SubscriptionPauseUntil
                    frequencyDays={
                      selectedSubscription.restaurant_variant_pack_subscription
                        ?.config.frequency_days ?? 1
                    }
                    nextFulfillmentDate={moment(
                      selectedSubscription.next_delivery_date,
                    )}
                    nextPlacementDate={
                      nextDeliveryDateData
                        ?.getNextDeliveryDateForSubscriptionSettings
                        .next_placement_date
                    }
                    onClose={closeSubscriptionActionSheet}
                    subscriptionUUID={selectedSubscriptionUUID}
                  />
                ) : null;
              case SubscriptionActionsRoute.Resume:
                return (
                  <ConfirmationActionContent
                    loading={
                      resumeSubscriptionLoading || nextDeliveryDateLoading
                    }
                    message={i18n.t('resume_orders.confirmation_message', {
                      date: nextFulfillmentDateIfResume,
                    })}
                    onPrimaryButtonPress={async () => {
                      try {
                        await resumeRestaurantVPSubscription({
                          variables: {
                            restaurantUUID,
                            restaurantVPSubscriptionUUID:
                              selectedSubscriptionUUID,
                            supplierUUID,
                          },
                        });
                      } catch {
                        showErrorToast();
                      } finally {
                        closeSubscriptionActionSheet();
                      }
                    }}
                    onSecondaryButtonPress={closeSubscriptionActionSheet}
                    primaryButtonLabel={i18n.t('resume')}
                    primaryButtonTrackingData={{
                      name: 'proceed_resume_subscription',
                      properties: {
                        subscriptionUUID: selectedSubscriptionUUID,
                      },
                    }}
                    secondaryButtonLabel={i18n.t('cancel')}
                    secondaryButtonTrackingData={{
                      name: 'cancel_resume_subscription',
                      properties: {
                        subscriptionUUID: selectedSubscriptionUUID,
                      },
                    }}
                    title={i18n.t('resume_orders.confirmation_title')}
                  />
                );
              default:
                return null;
            }
          }}
          routes={{
            [SubscriptionActionsRoute.SelectOperation]: {
              previousKey: null,
            },
            [SubscriptionActionsRoute.SubscriptionSettings]: {
              previousKey: null,
            },
            [SubscriptionActionsRoute.Pause]: {
              previousKey: SubscriptionActionsRoute.SelectOperation,
              showSmallLayoutBackButton: true,
              showSmallLayoutTitle: true,
              title: i18n.t('pause_for_how_long'),
            },
            [SubscriptionActionsRoute.Delete]: {
              previousKey: null,
            },
            [SubscriptionActionsRoute.SkipNextOrder]: {
              previousKey: null,
            },
            [SubscriptionActionsRoute.PauseUntilSpecificDay]: {
              previousKey: null,
            },
            [SubscriptionActionsRoute.Resume]: {
              previousKey: null,
            },
          }}
          titleStyle={styles.actionTitle}
        />
      )}
      {subscriptionMessageVisible ? (
        <BaseActionSheet isVisible onClose={closeSubscriptionIssueMessage}>
          <View style={styles.titleWrapper}>
            <Typography variant="heading-lg">{params?.title}</Typography>
          </View>
          <View style={styles.messageWrapper}>
            <Typography variant="paragraph-base">{params?.message}</Typography>
          </View>
          <Button
            onPress={closeSubscriptionIssueMessage}
            trackingData={{
              name: 'subscription_item_issue.got_it_button',
            }}
          >
            {i18n.t('got_it')}
          </Button>
        </BaseActionSheet>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  actionSheetContent: { paddingHorizontal: 0 },
  actionTitle: {
    paddingHorizontal: SCREEN_PADDING,
  },
  base: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  contentContainer: {
    paddingHorizontal: 16,
  },
  emptyState: {
    alignItems: 'center',
    gap: 12,
    justifyContent: 'center',
  },
  flex: {
    flex: 1,
  },
  listItem: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: 8,
    padding: 16,
  },
  listItemLarge: {
    marginBottom: 16,
    marginHorizontal: 8,
  },
  listItemSmall: {
    marginBottom: 8,
    paddingHorizontal: 16,
  },
  messageText: {
    color: theme.sk.color.text.secondary,
    lineHeight: 24,
    paddingHorizontal: 24,
    textAlign: 'center',
  },
  messageWrapper: {
    marginBottom: 24,
  },
  separator: {
    height: 0,
  },
  titleWrapper: {
    marginBottom: 8,
  },
}));
